import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  X,
  Plus,
  Home,
  Building,
  ChefHat,
  MapPin,
  GraduationCap,
  Utensils,
  Wine,
  Flame,
  Check,
  ChevronRight,
  ChevronLeft,
  ImagePlus,
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { amenities as allAmenities, cuisineTypes } from '../data/mockData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const experienceTypes = [
  { id: 'home-cooked', name: 'Home Cooked', icon: Home, desc: 'Intimate meal at my home' },
  { id: 'my-kitchen', name: 'My Kitchen', icon: Building, desc: 'At my kitchen venue' },
  { id: 'private-chef', name: 'Private Chef', icon: ChefHat, desc: 'I come to you' },
  { id: 'food-tour', name: 'Food Tour', icon: MapPin, desc: 'Guided culinary walk' },
  { id: 'cooking-class', name: 'Cooking Class', icon: GraduationCap, desc: 'Learn to cook' },
  { id: 'food-challenge', name: 'Food Challenge', icon: Utensils, desc: 'Multi-course experience' },
  { id: 'drinks-journey', name: 'Drinks Journey', icon: Wine, desc: 'Drinks & aperitivo journey' },
  { id: 'bbq-grill', name: 'BBQ & Grill', icon: Flame, desc: 'Outdoor grilling' },
];

const mealTypes = ['Breakfast', 'Brunch', 'Lunch', 'Dinner', 'Late Night', 'All Day'];

const categories = [
  'Home Cooked', 'Traditional', 'Food Tour', 'Aperitivo', 'Supper Club',
  'Farm to Table', 'Cooking Class', 'Drinks Journey', 'Street Food', 'BBQ & Grill',
  'Seafood', 'Vegan', 'Brunch', 'Dessert', 'Private Chef'
];

const HostPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, token } = useAuth();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [existingApplication, setExistingApplication] = useState(null);
  const [checkingApplication, setCheckingApplication] = useState(true);

  const [formData, setFormData] = useState({
    experienceType: '',
    title: '',
    description: '',
    location: '',
    cuisine: '',
    mealType: '',
    price: '',
    maxGuests: 8,
    duration: '2',
    amenities: [],
    category: '',
  });

  // Check for existing application on load
  useEffect(() => {
    const checkExistingApplication = async () => {
      if (!isAuthenticated || !token) {
        setCheckingApplication(false);
        return;
      }
      
      // If user is already a host, redirect to dashboard
      if (user?.role === 'host') {
        navigate('/dashboard');
        return;
      }
      
      try {
        const res = await axios.get(`${API_URL}/api/host-applications/mine`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setExistingApplication(res.data);
      } catch (err) {
        console.error("Failed to check existing host application:", err);
      } finally {
        setCheckingApplication(false);
      }
    };
    
    checkExistingApplication();
  }, [isAuthenticated, token, user, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleFiles = (files) => {
    const newImages = Array.from(files).map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setUploadedImages(prev => [...prev, ...newImages].slice(0, 10));
  };

  const removeImage = (index) => {
    setUploadedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to submit a host application');
      navigate('/login');
      return;
    }
    
    if (uploadedImages.length === 0) {
      toast.error('Please add at least one photo');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Upload images first
      const imageUrls = [];
      for (const img of uploadedImages) {
        const formDataImg = new FormData();
        formDataImg.append('file', img.file);
        const uploadRes = await axios.post(`${API_URL}/api/upload`, formDataImg, {
          headers: { Authorization: `Bearer ${token}` }
        });
        imageUrls.push(`${API_URL}${uploadRes.data.url}`);
      }
      
      // Submit host application
      await axios.post(`${API_URL}/api/host-applications`, {
        experience_type: formData.experienceType,
        title: formData.title,
        description: formData.description,
        location: formData.location,
        cuisine: formData.cuisine,
        meal_type: formData.mealType,
        price: parseFloat(formData.price),
        max_guests: formData.maxGuests,
        duration: `${formData.duration} hours`,
        amenities: formData.amenities,
        category: formData.category,
        images: imageUrls,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Host application submitted!', {
        description: 'An admin will review your application shortly.',
      });

      // Refresh to show pending status
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.experienceType !== '';
      case 2:
        return formData.title && formData.description && formData.location && formData.cuisine;
      case 3:
        return formData.maxGuests > 0 && formData.duration;
      case 4:
        return formData.amenities.length > 0;
      case 5:
        return uploadedImages.length > 0;
      case 6:
        return formData.price && parseFloat(formData.price) > 0 && formData.category && formData.mealType;
      default:
        return true;
    }
  };

  const totalSteps = 6;

  // Show loading state
  if (checkingApplication) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F4EDE4]">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#D65A31]" />
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F4EDE4]" data-testid="host-page">
        <Navbar />
        <div className="h-20" />
        <main className="flex-1 max-w-xl mx-auto px-6 py-12 w-full">
          <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
            <ChefHat className="w-16 h-16 text-[#D65A31] mx-auto mb-4" />
            <h1 className="text-2xl font-semibold mb-2">Become a Host</h1>
            <p className="text-gray-500 mb-6">
              Share your culinary passion and earn money by hosting unique dining experiences.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Please log in or create an account to submit your host application.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/login')} variant="outline">Log In</Button>
              <Button onClick={() => navigate('/register', { state: { intent: 'host' } })} className="bg-[#D65A31] hover:bg-[#B84A28] text-white">
                Sign Up
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Show existing application status
  if (existingApplication) {
    const statusConfig = {
      pending: {
        icon: Clock,
        color: 'text-yellow-600',
        bg: 'bg-yellow-50',
        title: 'Application Under Review',
        description: 'Your host application is being reviewed by our team. We\'ll notify you once a decision is made.',
      },
      approved: {
        icon: CheckCircle,
        color: 'text-green-600',
        bg: 'bg-green-50',
        title: 'Application Approved!',
        description: 'Congratulations! You\'re now a host. You can start creating experiences.',
      },
      rejected: {
        icon: XCircle,
        color: 'text-red-600',
        bg: 'bg-red-50',
        title: 'Application Not Approved',
        description: existingApplication.admin_notes || 'Unfortunately, your application was not approved at this time.',
      },
    };
    
    const config = statusConfig[existingApplication.status];
    const StatusIcon = config.icon;
    
    return (
      <div className="min-h-screen flex flex-col bg-[#F4EDE4]" data-testid="host-page">
        <Navbar />
        <div className="h-20" />
        <main className="flex-1 max-w-xl mx-auto px-6 py-12 w-full">
          <div className={`${config.bg} rounded-2xl p-8 text-center`}>
            <StatusIcon className={`w-16 h-16 ${config.color} mx-auto mb-4`} />
            <h1 className="text-2xl font-semibold mb-2">{config.title}</h1>
            <p className="text-gray-600 mb-6">{config.description}</p>
            
            <div className="bg-white rounded-xl p-4 text-left mb-6">
              <h3 className="font-medium mb-2">Your Application:</h3>
              <p className="text-sm text-gray-600"><strong>Experience:</strong> {existingApplication.title}</p>
              <p className="text-sm text-gray-600"><strong>Type:</strong> {existingApplication.experience_type}</p>
              <p className="text-sm text-gray-600"><strong>Location:</strong> {existingApplication.location}</p>
              <p className="text-sm text-gray-600"><strong>Submitted:</strong> {new Date(existingApplication.created_at).toLocaleDateString()}</p>
            </div>
            
            {existingApplication.status === 'approved' && (
              <Button 
                onClick={() => navigate('/dashboard')} 
                className="bg-[#D65A31] hover:bg-[#B84A28] text-white"
              >
                Go to Dashboard
              </Button>
            )}
            
            {existingApplication.status === 'rejected' && (
              <p className="text-sm text-gray-500">
                You may contact support if you have questions about this decision.
              </p>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F4EDE4]" data-testid="host-page">
      <Navbar />
      <div className="h-20" />
      <Toaster />

      {/* Progress Bar */}
      <div className="fixed top-20 left-0 right-0 h-1 bg-gray-200 z-40">
        <div
          className="h-full bg-[#D65A31] transition-all duration-300"
          style={{ width: `${(step / totalSteps) * 100}%` }}
          data-testid="progress-bar"
        />
      </div>

      <main className="flex-1 max-w-3xl mx-auto px-6 py-12 w-full">
        {/* Step 1: Experience Type */}
        {step === 1 && (
          <div className="animate-in fade-in duration-300" data-testid="step-1">
            <h1 className="text-3xl font-semibold mb-2">What type of experience will you host?</h1>
            <p className="text-gray-500 mb-8">Choose the format that best describes your culinary offering</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {experienceTypes.map((type) => {
                const IconComponent = type.icon;
                const isSelected = formData.experienceType === type.id;
                return (
                  <button
                    key={type.id}
                    onClick={() => handleInputChange('experienceType', type.id)}
                    className={`p-4 border-2 rounded-xl flex flex-col items-center gap-2 transition-all hover:border-[#D65A31] relative ${
                      isSelected ? 'border-[#D65A31] bg-[#FDF8F4]' : 'border-gray-200'
                    }`}
                    data-testid={`experience-type-${type.id}`}
                  >
                    <IconComponent className="w-8 h-8" />
                    <span className="font-medium text-sm text-center">{type.name}</span>
                    <span className="text-xs text-gray-500 text-center">{type.desc}</span>
                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-[#D65A31] rounded-full p-1">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Basic Info */}
        {step === 2 && (
          <div className="animate-in fade-in duration-300" data-testid="step-2">
            <h1 className="text-3xl font-semibold mb-2">Tell us about your experience</h1>
            <p className="text-gray-500 mb-8">Share the details that will make diners excited</p>

            <div className="space-y-6">
              <div>
                <Label htmlFor="title" className="text-base font-medium">Experience title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="e.g., Authentic Italian Pasta Making with Nonna"
                  className="mt-2 py-6 text-base"
                  maxLength={60}
                  data-testid="title-input"
                />
                <p className="text-sm text-gray-500 mt-1">{formData.title.length}/60</p>
              </div>

              <div>
                <Label htmlFor="description" className="text-base font-medium">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe what guests will experience, taste, and learn..."
                  className="mt-2 min-h-[150px] text-base"
                  maxLength={500}
                  data-testid="description-input"
                />
                <p className="text-sm text-gray-500 mt-1">{formData.description.length}/500</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location" className="text-base font-medium">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="City, State"
                    className="mt-2 py-6 text-base"
                    data-testid="location-input"
                  />
                </div>
                <div>
                  <Label className="text-base font-medium">Cuisine Type</Label>
                  <Select
                    value={formData.cuisine}
                    onValueChange={(value) => handleInputChange('cuisine', value)}
                  >
                    <SelectTrigger className="mt-2 py-6" data-testid="cuisine-select">
                      <SelectValue placeholder="Select cuisine" />
                    </SelectTrigger>
                    <SelectContent>
                      {cuisineTypes.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Capacity & Duration */}
        {step === 3 && (
          <div className="animate-in fade-in duration-300" data-testid="step-3">
            <h1 className="text-3xl font-semibold mb-2">Set your capacity</h1>
            <p className="text-gray-500 mb-8">How many guests can you accommodate?</p>

            <div className="space-y-6">
              <div className="flex items-center justify-between py-4 border-b">
                <div>
                  <span className="text-lg font-medium">Maximum guests</span>
                  <p className="text-sm text-gray-500">The most diners you can host</p>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => handleInputChange('maxGuests', Math.max(1, formData.maxGuests - 1))}
                    className="w-10 h-10 rounded-full border border-gray-400 flex items-center justify-center hover:border-gray-900 transition-colors disabled:opacity-50"
                    disabled={formData.maxGuests <= 1}
                    data-testid="guests-minus-btn"
                  >
                    -
                  </button>
                  <span className="text-lg font-medium w-8 text-center" data-testid="guests-count">{formData.maxGuests}</span>
                  <button
                    onClick={() => handleInputChange('maxGuests', Math.min(50, formData.maxGuests + 1))}
                    className="w-10 h-10 rounded-full border border-gray-400 flex items-center justify-center hover:border-gray-900 transition-colors"
                    data-testid="guests-plus-btn"
                  >
                    +
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-base font-medium">Duration</Label>
                <div className="flex flex-wrap gap-3 mt-3">
                  {['1', '1.5', '2', '2.5', '3', '4', '5', '6+'].map((d) => (
                    <button
                      key={d}
                      onClick={() => handleInputChange('duration', d)}
                      className={`px-6 py-3 rounded-full border transition-colors ${
                        formData.duration === d
                          ? 'bg-gray-900 text-white border-gray-900'
                          : 'border-gray-300 hover:border-gray-600'
                      }`}
                      data-testid={`duration-${d}`}
                    >
                      {d} {d === '1' ? 'hour' : 'hours'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: What's Included */}
        {step === 4 && (
          <div className="animate-in fade-in duration-300" data-testid="step-4">
            <h1 className="text-3xl font-semibold mb-2">What's included?</h1>
            <p className="text-gray-500 mb-8">Select everything guests will enjoy</p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {allAmenities.map((amenity) => {
                const isSelected = formData.amenities.includes(amenity);
                return (
                  <button
                    key={amenity}
                    onClick={() => toggleAmenity(amenity)}
                    className={`p-4 border-2 rounded-xl text-left transition-all hover:border-[#D65A31] ${
                      isSelected ? 'border-[#D65A31] bg-[#FDF8F4]' : 'border-gray-200'
                    }`}
                    data-testid={`amenity-${amenity.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox checked={isSelected} className="pointer-events-none" />
                      <span className="text-sm font-medium">{amenity}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 5: Photos */}
        {step === 5 && (
          <div className="animate-in fade-in duration-300" data-testid="step-5">
            <h1 className="text-3xl font-semibold mb-2">Show off your experience</h1>
            <p className="text-gray-500 mb-8">Add mouthwatering photos. You can add up to 10.</p>

            <div
              className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                dragActive ? 'border-[#D65A31] bg-[#FDF8F4]' : 'border-gray-300'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="photo-drop-zone"
            >
              <ImagePlus className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium mb-2">Drag your photos here</p>
              <p className="text-gray-500 mb-4">or</p>
              <label className="inline-block">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleFiles(e.target.files)}
                  className="hidden"
                  data-testid="photo-input"
                />
                <span className="bg-[#D65A31] text-white px-6 py-3 rounded-lg cursor-pointer hover:bg-[#B84A28] transition-colors">
                  Upload from your device
                </span>
              </label>
            </div>

            {uploadedImages.length > 0 && (
              <div className="mt-8">
                <h3 className="font-medium mb-4">{uploadedImages.length} photo{uploadedImages.length !== 1 ? 's' : ''} uploaded</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="uploaded-images">
                  {uploadedImages.map((image, index) => (
                    <div key={index} className="relative aspect-square rounded-xl overflow-hidden group">
                      <img src={image.preview} alt={image.name} className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 bg-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-gray-100"
                        data-testid={`remove-image-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                      {index === 0 && (
                        <div className="absolute bottom-2 left-2 bg-white px-2 py-1 rounded text-xs font-medium">
                          Cover photo
                        </div>
                      )}
                    </div>
                  ))}
                  {uploadedImages.length < 10 && (
                    <label className="aspect-square border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-[#D65A31] transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => handleFiles(e.target.files)}
                        className="hidden"
                      />
                      <Plus className="w-8 h-8 text-gray-400" />
                      <span className="text-sm text-gray-500 mt-2">Add more</span>
                    </label>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 6: Pricing */}
        {step === 6 && (
          <div className="animate-in fade-in duration-300" data-testid="step-6">
            <h1 className="text-3xl font-semibold mb-2">Set your price</h1>
            <p className="text-gray-500 mb-8">Price per person - you can adjust anytime</p>

            <div className="space-y-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center border-2 border-[#D65A31] rounded-xl px-8 py-6">
                  <span className="text-5xl font-semibold">$</span>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    placeholder="0"
                    className="text-5xl font-semibold w-40 outline-none text-center"
                    min="1"
                    data-testid="price-input"
                  />
                </div>
                <p className="text-gray-500 mt-2">per person</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-base font-medium">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => handleInputChange('category', value)}
                  >
                    <SelectTrigger className="mt-2 py-6" data-testid="category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-base font-medium">Meal Type</Label>
                  <Select
                    value={formData.mealType}
                    onValueChange={(value) => handleInputChange('mealType', value)}
                  >
                    <SelectTrigger className="mt-2 py-6" data-testid="meal-type-select">
                      <SelectValue placeholder="Select meal type" />
                    </SelectTrigger>
                    <SelectContent>
                      {mealTypes.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-xl p-6 mt-8" data-testid="listing-summary">
                <h3 className="font-semibold mb-4">Experience summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Type</span>
                    <span className="font-medium capitalize">{formData.experienceType.replace('-', ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Cuisine</span>
                    <span className="font-medium">{formData.cuisine}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Location</span>
                    <span className="font-medium">{formData.location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Capacity</span>
                    <span className="font-medium">Up to {formData.maxGuests} guests</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Duration</span>
                    <span className="font-medium">{formData.duration} hours</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Photos</span>
                    <span className="font-medium">{uploadedImages.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Navigation Footer */}
      <div className="sticky bottom-0 bg-white border-t py-4 px-6">
        <div className="max-w-3xl mx-auto flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={() => setStep(step - 1)}
            disabled={step === 1}
            className="underline font-medium"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          {step < totalSteps ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="bg-[#D65A31] hover:bg-[#B84A28] text-white px-8 py-6 rounded-lg font-medium"
              data-testid="next-btn"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || isSubmitting}
              className="bg-gradient-to-r from-[#D65A31] to-[#FF6B35] hover:from-[#B84A28] hover:to-[#C45A20] text-white px-8 py-6 rounded-lg font-medium"
              data-testid="publish-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                'Publish experience'
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default HostPage;
